import { useCallback, useMemo, useState } from 'react';
import {
  type ColumnDef,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ChevronsUpDown, FileText, Search, Upload } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

import { useDocuments } from '../../hooks/useDocuments';
import { documentDisplayName, truncateMiddle } from '../../lib/documentName';
import { summarizeError } from '../../lib/errorMessage';
import type { DocumentResponse } from '../../types';
import { EmptyState } from '../EmptyState';
import { DeleteDocumentDialog } from './DeleteDocumentDialog';
import { DocumentDetailSheet } from './DocumentDetailSheet';
import { DocumentRowMenu } from './DocumentRowMenu';
import { DocumentStatusBadge } from './DocumentStatusBadge';

function formatDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function useColumns(
  collectionId: string,
  onViewDetails: (doc: DocumentResponse) => void,
  onRequestDelete: (doc: DocumentResponse) => void,
): ColumnDef<DocumentResponse>[] {
  return useMemo(
    () => [
      {
        accessorKey: 'source_uri',
        header: 'Arquivo',
        cell: ({ row }) => {
          const name = documentDisplayName(row.original);
          return (
            <div className="flex max-w-sm items-center gap-2.5">
              <FileText className="text-muted-foreground size-4 shrink-0" />
              {/* title dá o nome inteiro no hover — a célula trunca por CSS. */}
              <span className="truncate text-sm" title={name}>
                {name}
              </span>
            </div>
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const { status, error_message } = row.original;
          return (
            // Largura teto na coluna: o erro de indexação vem com caminho de
            // arquivo e trecho de stack, e sem isso empurrava a tabela inteira
            // pra fora da tela.
            <div className="flex w-56 max-w-56 min-w-0 flex-col items-start gap-1">
              <DocumentStatusBadge status={status} />
              {status === 'failed' && error_message && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewDetails(row.original);
                  }}
                  // truncate, não wrap: uma linha só, com reticências. Quebrar
                  // em várias linhas esticava a altura da lista e ainda deixava
                  // vazar quando o erro vinha sem espaço nenhum.
                  className="text-destructive block max-w-full cursor-pointer truncate text-left text-xs hover:underline"
                  title="Ver detalhes do erro"
                >
                  {summarizeError(error_message)}
                </button>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'chunk_count',
        header: 'Chunks',
        cell: ({ getValue }) => (
          <span className="text-muted-foreground text-sm tabular-nums">
            {(getValue() as number | null) ?? '—'}
          </span>
        ),
      },
      {
        accessorKey: 'progress',
        header: 'Progresso',
        enableSorting: false,
        cell: ({ row }) => {
          const { status, progress } = row.original;
          if (status === 'indexed') {
            return <span className="text-muted-foreground text-sm tabular-nums">100%</span>;
          }
          if (status !== 'processing') {
            return <span className="text-muted-foreground text-sm">—</span>;
          }
          // O parsing do PDF, que é a etapa mais longa, acontece antes de haver
          // fração para mostrar. Enquanto ela não chega, dizer "iniciando" é mais
          // honesto que uma barra parada em 0%, que parecia travamento.
          if (progress <= 0) {
            return <span className="text-muted-foreground text-sm">Iniciando…</span>;
          }
          const pct = Math.round(progress * 100);
          return (
            <div className="flex w-28 items-center gap-2">
              <Progress value={pct} className="h-1.5" />
              <span className="text-muted-foreground w-9 shrink-0 text-right text-xs tabular-nums">
                {pct}%
              </span>
            </div>
          );
        },
      },
      {
        accessorKey: 'created_at',
        header: 'Criado em',
        cell: ({ getValue }) => (
          <span className="text-muted-foreground text-sm">
            {formatDate(getValue() as string | null)}
          </span>
        ),
      },
      {
        id: 'actions',
        cell: ({ row }) => (
          <DocumentRowMenu
            collectionId={collectionId}
            document={row.original}
            onViewDetails={onViewDetails}
            onRequestDelete={onRequestDelete}
          />
        ),
      },
    ],
    [collectionId, onViewDetails, onRequestDelete],
  );
}

interface Props {
  collectionId: string;
  onUploadClick: () => void;
}

export function DocumentTable({ collectionId, onUploadClick }: Props) {
  const { data: docs, isLoading } = useDocuments(collectionId);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState<SortingState>([]);
  const [toDeleteId, setToDeleteId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Guardar o id e derivar o documento da lista, em vez de guardar o objeto:
  // o objeto vira um retrato do instante do clique, e o painel de detalhes
  // continuava mostrando o status antigo enquanto a linha na tabela já tinha
  // mudado pelo polling.
  const selected = docs?.find((d) => d.id === selectedId) ?? null;
  const toDelete = docs?.find((d) => d.id === toDeleteId) ?? null;

  const handleViewDetails = useCallback((doc: DocumentResponse) => setSelectedId(doc.id), []);
  const handleRequestDelete = useCallback((doc: DocumentResponse) => setToDeleteId(doc.id), []);

  const columns = useColumns(collectionId, handleViewDetails, handleRequestDelete);

  // eslint-disable-next-line react-hooks/incompatible-library -- known limitation: useReactTable returns non-memoizable functions; safe here as DocumentTable is not memo'd
  const table = useReactTable({
    data: docs ?? [],
    columns,
    state: { globalFilter, sorting },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    globalFilterFn: (row, _columnId, value: string) =>
      documentDisplayName(row.original).toLowerCase().includes(value.toLowerCase()),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const rows = table.getRowModel().rows;
  const hasDocs = Boolean(docs && docs.length > 0);

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center justify-between gap-3 px-6 py-3">
        <span className="text-muted-foreground shrink-0 text-xs font-medium tracking-wide uppercase">
          Documentos{docs ? ` (${docs.length})` : ''}
        </span>
        {hasDocs && (
          <div className="relative">
            <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2" />
            <Input
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder="Buscar arquivo…"
              className="h-8 w-56 pl-8 text-sm"
              aria-label="Buscar documento por nome"
            />
          </div>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {isLoading && (
          <div className="space-y-2 px-6 py-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        )}

        {!isLoading && !hasDocs && (
          <EmptyState
            icon={<FileText />}
            title="Sem documentos"
            description="Envie arquivos ou arraste-os pra qualquer lugar desta área."
            action={
              <Button size="sm" onClick={onUploadClick}>
                <Upload className="mr-1 size-4" />
                Enviar arquivo
              </Button>
            }
          />
        )}

        {hasDocs && rows.length === 0 && (
          <EmptyState
            icon={<Search />}
            title="Nenhum resultado"
            // Termo truncado: colado cru, um texto longo esticava o container e
            // gerava rolagem horizontal na lista inteira.
            description={`Nenhum documento corresponde a "${truncateMiddle(globalFilter)}".`}
          />
        )}

        {hasDocs && rows.length > 0 && (
          // Células no mesmo px-6 do cabeçalho (o default do shadcn é p-2, que
          // deixava a coluna "Arquivo" desalinhada de tudo acima) e linhas mais
          // altas — lista de arquivos com linha apertada cansa de ler.
          <Table className="[&_td]:px-6 [&_td]:py-3 [&_th]:px-6">
            <TableHeader>
              {table.getHeaderGroups().map((hg) => (
                <TableRow key={hg.id}>
                  {hg.headers.map((h) => {
                    const canSort = h.column.getCanSort();
                    const sorted = h.column.getIsSorted();
                    return (
                      <TableHead key={h.id}>
                        {h.isPlaceholder ? null : canSort ? (
                          <button
                            type="button"
                            onClick={h.column.getToggleSortingHandler()}
                            className="hover:text-foreground flex cursor-pointer items-center gap-1 transition-colors"
                          >
                            {flexRender(h.column.columnDef.header, h.getContext())}
                            <ChevronsUpDown
                              className={cn(
                                'size-3',
                                sorted ? 'text-foreground' : 'text-muted-foreground/50',
                              )}
                            />
                          </button>
                        ) : (
                          flexRender(h.column.columnDef.header, h.getContext())
                        )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.id}
                  onClick={() => setSelectedId(row.original.id)}
                  className="hover:bg-muted/40 cursor-pointer transition-colors"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <DeleteDocumentDialog
        collectionId={collectionId}
        document={toDelete}
        onOpenChange={(open) => {
          if (!open) setToDeleteId(null);
        }}
      />

      <DocumentDetailSheet
        document={selected}
        onOpenChange={(open) => {
          if (!open) setSelectedId(null);
        }}
      />
    </div>
  );
}
